import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { news } from '@/mocks/news';
import { videos } from '@/mocks/videos';
import { specials } from '@/mocks/specials';
import { categories } from '@/mocks/categories';
import NewsCard from '@/components/feature/NewsCard';
import VideoCard from '@/components/feature/VideoCard';
import SpecialCard from '@/components/feature/SpecialCard';
import SectionHeader from '@/components/feature/SectionHeader';
import HeroCarousel, { type HeroSlide } from '@/pages/home/components/HeroCarousel';
import HeadlineFocus from '@/pages/home/components/HeadlineFocus';
import SiteIntro from '@/pages/home/components/SiteIntro';

const container = 'w-full max-w-[1280px] mx-auto px-4 md:px-8';

export default function Home() {
  usePageMeta(
    '91.con - 多元娱乐资讯与内容平台',
    '91.con提供最新娱乐资讯、多样化视频资源和互动内容，涵盖影视、综艺及热点新闻，为用户打造丰富多彩的娱乐体验。',
    '91.con,娱乐网站,娱乐资讯,视频资源,影视综艺,热点新闻,多元内容'
  );

  const featuredNews = news.filter((n) => n.featured).slice(0, 3);
  const featuredVideos = videos.filter((v) => v.featured).slice(0, 2);

  const slides: HeroSlide[] = [
    ...featuredNews.map((n) => ({
      id: n.id,
      title: n.title,
      summary: n.summary,
      image: n.cover,
      to: `/news/${n.id}`,
      tag: `${n.categoryName} · 热点资讯`,
    })),
    ...featuredVideos.map((v) => ({
      id: v.id,
      title: v.title,
      summary: v.description,
      image: v.cover,
      to: `/video/${v.id}`,
      tag: `${v.typeName} · 视频推荐`,
    })),
  ];

  const lead = news[0];
  const side = news.slice(1, 5);
  const gridNews = news.slice(5, 11);
  const recommendVideos = [...videos.filter((v) => v.featured), ...videos.filter((v) => !v.featured)].slice(0, 6);
  const homeSpecials = specials.slice(0, 3);

  return (
    <>
      <HeroCarousel slides={slides} />

      <section className="w-full bg-background-50 border-b border-background-200">
        <div className={`${container} py-5`}>
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <span className="text-sm text-foreground-500 whitespace-nowrap shrink-0 mr-1">频道导航</span>
            {categories.map((c) => (
              <Link
                key={c.key}
                to={`/category/${c.key}`}
                className="px-4 py-2 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-700 hover:text-primary-400 hover:border-primary-500/60 transition-colors cursor-pointer whitespace-nowrap shrink-0"
              >
                {c.name}
              </Link>
            ))}
            <Link
              to="/videos"
              className="px-4 py-2 rounded-full bg-secondary-100 border border-background-300 text-sm text-secondary-900 hover:border-accent-500/60 transition-colors cursor-pointer whitespace-nowrap shrink-0"
            >
              视频专区
            </Link>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className={`${container} py-12 md:py-14`}>
          <SectionHeader
            title="最新资讯快报"
            subtitle="第一时间掌握影视、综艺与娱乐圈的热点动态"
            moreTo="/category/film"
          />
          <HeadlineFocus lead={lead} side={side} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mt-6">
            {gridNews.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 border-y border-background-200">
        <div className={`${container} py-12 md:py-14`}>
          <SectionHeader
            title="视频推荐"
            subtitle="影视剧、综艺与短视频精选，点击即可查看详情"
            moreTo="/videos"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {recommendVideos.map((item) => (
              <VideoCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className={`${container} py-12 md:py-14`}>
          <SectionHeader
            title="热门专题"
            subtitle="围绕热点主题聚合资讯与视频，一次看够"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {homeSpecials.map((item) => (
              <SpecialCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      <SiteIntro />
    </>
  );
}